/**
 * StorePage.jsx - Garage Parts Store Public Page
 * 
 * Lists all spare parts offered by a specific garage.
 * Accessed via /:garageSlug/store
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Spinner } from '../components/ui/spinner';
import { formatCurrency } from '../lib/i18n';
import { Search, Package, Phone, Wrench, Globe, ArrowLeft, MessageSquare } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category label map for known category values (used as display names)
const CATEGORY_LABELS = {
  engine: { label: 'Engine', labelBn: 'ইঞ্জিন' },
  brake: { label: 'Brake', labelBn: 'ব্রেক' },
  electrical: { label: 'Electrical', labelBn: 'ইলেকট্রিক্যাল' },
  transmission: { label: 'Transmission', labelBn: 'ট্রান্সমিশন' },
  accessories: { label: 'Accessories', labelBn: 'আনুষঙ্গিক' },
  lubricants: { label: 'Lubricants', labelBn: 'লুব্রিকেন্টস' },
  oils: { label: 'Oils & Lubricants', labelBn: 'তেল ও লুব্রিকেন্ট' },
  brakes: { label: 'Brake Parts', labelBn: 'ব্রেক পার্টস' },
  filters: { label: 'Filters', labelBn: 'ফিল্টার' },
  cables: { label: 'Cables', labelBn: 'কেবল' },
};

export default function StorePageLegacy() {
  const { garageSlug } = useParams();
  const navigate = useNavigate();
  const { t, language, toggleLanguage } = useLanguage();
  
  const [parts, setParts] = useState([]);
  const [garageInfo, setGarageInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [notFound, setNotFound] = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  const [requestForm, setRequestForm] = useState({ customer_name: '', customer_phone: '', part_name: '', bike_make: '', bike_model: '', notes: '' });
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [categories, setCategories] = useState([{ value: 'all', label: 'All Categories', labelBn: 'সব ক্যাটাগরি' }]);
  const [badgeOpacity, setBadgeOpacity] = useState(1);
  const scrollTimer = useRef(null);

  // Scroll-fade for watermark badge
  useEffect(() => {
    const onScroll = () => {
      setBadgeOpacity(0.15);
      if (scrollTimer.current) clearTimeout(scrollTimer.current);
      scrollTimer.current = setTimeout(() => setBadgeOpacity(1), 400);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); if (scrollTimer.current) clearTimeout(scrollTimer.current); };
  }, []);

  useEffect(() => {
    if (garageSlug) {
      fetchData();
    }
  }, [garageSlug]);

  const fetchData = async () => {
    try {
      const [garageRes, partsRes] = await Promise.all([
        axios.get(`${API}/tenants/${garageSlug}/public`),
        axios.get(`${API}/tenants/${garageSlug}/parts`),
      ]);
      setGarageInfo(garageRes.data);
      const partsData = partsRes.data;
      setParts(partsData);
      // Build category filters from actual part categories
      const catSet = new Set();
      partsData.forEach(p => { if (p.category) catSet.add(p.category); });
      const dynamicCats = [...catSet].sort().map(val => {
        const known = CATEGORY_LABELS[val];
        const displayLabel = known ? known.label : val.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        const displayLabelBn = known ? known.labelBn : displayLabel;
        return { value: val, label: displayLabel, labelBn: displayLabelBn };
      });
      setCategories([{ value: 'all', label: 'All Categories', labelBn: 'সব ক্যাটাগরি' }, ...dynamicCats]);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      if (error.response?.status === 404) {
        setNotFound(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // Redirect if no slug
  if (!garageSlug) {
    navigate('/');
    return null;
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Spinner size="lg" />
      </div>
    );
  }

  // Not found state
  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-4">
        <h1 className="text-4xl font-bold text-slate-900 mb-4">
          {language === 'en' ? 'Garage Not Found' : 'গ্যারেজ পাওয়া যায়নি'}
        </h1>
        <Link to="/">
          <Button className="bg-emerald-600 hover:bg-emerald-700">
            {language === 'en' ? 'Go to MotoPotti' : 'মোটোপটিতে যান'}
          </Button>
        </Link>
      </div>
    );
  }

  const garage = garageInfo || {};
  const garageName = language === 'bn' && garage.name_bn ? garage.name_bn : garage.name;
  const basePath = `/${garageSlug}`;

  // Filter parts
  const filteredParts = parts.filter(part => {
    const matchesCategory = category === 'all' || part.category === category;
    const matchesSearch = !search || 
      part.name.toLowerCase().includes(search.toLowerCase()) ||
      part.name_bn?.includes(search) ||
      part.brand?.toLowerCase().includes(search.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Call garage
  const callGarage = () => {
    window.location.href = `tel:${garage.phone}`;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to={basePath} className="flex items-center gap-2" data-testid="logo-link">
              {garage.logo_url ? (
                <img src={garage.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
              ) : (
                <div className="w-10 h-10 bg-blue-800 rounded-lg flex items-center justify-center">
                  <Wrench className="w-6 h-6 text-white" />
                </div>
              )}
              <span className="text-xl font-bold text-slate-900 tracking-tight font-['Barlow_Condensed']">
                {garageName}
              </span>
            </Link>
            <div className="hidden md:flex items-center gap-6">
              <Link to={basePath} className="text-sm font-medium text-slate-600 hover:text-blue-800">
                {language === 'en' ? 'Home' : 'হোম'}
              </Link>
              <Link to={`${basePath}/services`} className="text-sm font-medium text-slate-600 hover:text-blue-800">
                {language === 'en' ? 'Services' : 'সার্ভিস'}
              </Link>
              <Link to={`${basePath}/store`} className="text-sm font-medium text-blue-800">
                {language === 'en' ? 'Parts' : 'পার্টস'}
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={toggleLanguage} className="gap-2">
                <Globe className="w-4 h-4" />
                <span className="hidden sm:inline">{language === 'en' ? 'বাংলা' : 'English'}</span>
              </Button>
              <Button onClick={callGarage} className="bg-blue-800 hover:bg-blue-900 gap-2">
                <Phone className="w-4 h-4" />
                <span className="hidden sm:inline">{language === 'en' ? 'Call Now' : 'কল করুন'}</span>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-12 bg-gradient-to-br from-blue-900 to-blue-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to={basePath} className="inline-flex items-center text-blue-200 hover:text-white mb-4 gap-1">
            <ArrowLeft className="w-4 h-4" />
            {language === 'en' ? 'Back to Home' : 'হোমে ফিরে যান'}
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold text-white font-['Barlow_Condensed']">
            {language === 'en' ? 'Spare Parts Store' : 'খুচরা যন্ত্রাংশের দোকান'}
          </h1>
          <p className="text-blue-100 mt-2 max-w-2xl">
            {language === 'en' 
              ? 'Genuine and high-quality spare parts for all motorcycle brands.'
              : 'সব মোটরসাইকেল ব্র্যান্ডের জন্য আসল এবং উচ্চমানের খুচরা যন্ত্রাংশ।'}
          </p>
        </div>
      </section>

      {/* Search & Filter Section */}
      <section className="py-6 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input 
                placeholder={language === 'en' ? 'Search parts by name or brand...' : 'নাম বা ব্র্যান্ড দিয়ে খুঁজুন...'}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-full md:w-[200px]" data-testid="category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {language === 'bn' ? cat.labelBn : cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Parts Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <p className="text-slate-600">
              {filteredParts.length} {language === 'en' ? 'parts found' : 'টি পার্টস পাওয়া গেছে'}
            </p>
          </div>

          {filteredParts.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">
                {language === 'en' ? 'No parts found matching your criteria' : 'আপনার মানদণ্ডের সাথে মিলে এমন কোনো পার্টস পাওয়া যায়নি'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {filteredParts.map((part) => (
                <Card key={part.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    {/* Brand/Category Badge */}
                    <Badge className="mb-3 bg-slate-100 text-slate-700">
                      {part.brand || part.category}
                    </Badge>
                    
                    {/* Part Name */}
                    <h3 className="font-bold text-slate-900 mb-1 line-clamp-2">
                      {language === 'bn' && part.name_bn ? part.name_bn : part.name}
                    </h3>
                    
                    {/* SKU */}
                    {part.sku && (
                      <p className="text-xs text-slate-500 mb-2">SKU: {part.sku}</p>
                    )}
                    
                    {/* Price */}
                    <p className="text-xl font-bold text-blue-800 mb-2">
                      {formatCurrency(part.selling_price || part.price, language)}
                    </p>
                    
                    {/* Stock Status */}
                    {(part.stock_quantity || part.quantity) > 0 ? (
                      <Badge className="bg-green-100 text-green-800">
                        {language === 'en' ? `${part.stock_quantity || part.quantity} in stock` : `${part.stock_quantity || part.quantity} টি স্টকে`}
                      </Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-800">
                        {language === 'en' ? 'Out of Stock' : 'স্টক শেষ'}
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4 font-['Barlow_Condensed']">
            {language === 'en' ? "Can't Find What You Need?" : 'যা প্রয়োজন তা খুঁজে পাচ্ছেন না?'}
          </h2>
          <p className="text-slate-600 mb-6">
            {language === 'en' 
              ? 'Request a part and we will notify you when it becomes available.'
              : 'একটি পার্ট অনুরোধ করুন এবং আমরা আপনাকে জানাব যখন এটি পাওয়া যাবে।'}
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button 
              size="lg" 
              onClick={() => setShowRequest(true)}
              className="bg-emerald-600 hover:bg-emerald-700 gap-2"
            >
              <MessageSquare className="w-5 h-5" />
              {t('marketing_request_part')}
            </Button>
            <Button 
              size="lg" 
              onClick={callGarage}
              variant="outline"
              className="gap-2"
            >
              <Phone className="w-5 h-5" />
              {garage.phone}
            </Button>
          </div>
        </div>
      </section>

      {/* MotoPotti Floating Badge — iOS Liquid Glass */}
      <Link
        to="/"
        className="fixed bottom-5 right-5 z-50 group flex items-center gap-1.5 hover:gap-2.5 rounded-full py-1.5 pl-1.5 pr-2.5 hover:pr-3.5 transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] cursor-pointer"
        style={{
          opacity: badgeOpacity,
          transition: 'opacity 0.5s ease, gap 0.5s cubic-bezier(0.34,1.56,0.64,1), padding 0.5s cubic-bezier(0.34,1.56,0.64,1)',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.72) 0%, rgba(255,255,255,0.45) 40%, rgba(240,249,255,0.55) 100%)',
          backdropFilter: 'blur(50px) saturate(220%) brightness(1.15) contrast(1.05)',
          WebkitBackdropFilter: 'blur(50px) saturate(220%) brightness(1.15) contrast(1.05)',
          border: '0.5px solid rgba(255,255,255,0.7)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.06), 0 1.5px 6px rgba(0,0,0,0.03), inset 0 1.5px 0 rgba(255,255,255,0.9), inset 0 -0.5px 0 rgba(0,0,0,0.04)',
        }}
        title="MotoPotti"
      >
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
          style={{
            background: 'linear-gradient(145deg, #34d399 0%, #0d9488 100%)',
            boxShadow: '0 2px 8px rgba(13,148,136,0.3), inset 0 1px 0 rgba(255,255,255,0.3)',
          }}
        >
          <Wrench className="w-3 h-3 text-white drop-shadow-sm" />
        </div>
        <div className="flex flex-col leading-none">
          <span className="text-[9px] text-slate-400/70 font-medium">{language === 'en' ? 'Powered by' : 'পরিচালিত'}</span>
          <span className="text-[12px] font-bold text-slate-600 tracking-tight">MotoPotti</span>
        </div>
      </Link>

      {/* Floating Request Button — positioned above watermark */}
      <button
        onClick={() => setShowRequest(true)}
        className="fixed bottom-[72px] right-5 z-50 bg-emerald-600 hover:bg-emerald-700 text-white p-3.5 rounded-full shadow-lg transition-all hover:scale-110 hover:shadow-xl"
        style={{ opacity: badgeOpacity, transition: 'opacity 0.5s ease, transform 0.2s ease, box-shadow 0.2s ease' }}
        aria-label={t('marketing_request_part')}
      >
        <MessageSquare className="w-5 h-5" />
      </button>

      {/* Parts Request Dialog */}
      <Dialog open={showRequest} onOpenChange={(v) => { setShowRequest(v); if (!v) setRequestSuccess(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('marketing_request_part')}</DialogTitle>
          </DialogHeader>
          {requestSuccess ? (
            <div className="py-8 text-center">
              <div className="text-4xl mb-3">✅</div>
              <p className="font-medium">{t('marketing_request_submitted')}</p>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                <div>
                  <Label>{language === 'en' ? 'Your Name' : 'আপনার নাম'} *</Label>
                  <Input value={requestForm.customer_name} onChange={e => setRequestForm(f => ({ ...f, customer_name: e.target.value }))} />
                </div>
                <div>
                  <Label>{t('phone')} *</Label>
                  <Input type="tel" maxLength={11} placeholder="01XXXXXXXXX" value={requestForm.customer_phone}
                    onChange={e => setRequestForm(f => ({ ...f, customer_phone: e.target.value }))} />
                </div>
                <div>
                  <Label>{language === 'en' ? 'Part Name' : 'পার্টের নাম'} *</Label>
                  <Input value={requestForm.part_name} onChange={e => setRequestForm(f => ({ ...f, part_name: e.target.value }))}
                    placeholder={language === 'en' ? 'e.g. Engine oil, brake pad...' : 'যেমন: ইঞ্জিন অয়েল, ব্রেক প্যাড...'} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{language === 'en' ? 'Vehicle Make' : 'বাইক মেক'}</Label>
                    <Input value={requestForm.bike_make} onChange={e => setRequestForm(f => ({ ...f, bike_make: e.target.value }))}
                      placeholder={language === 'en' ? 'e.g. Honda' : 'যেমন: Honda'} />
                  </div>
                  <div>
                    <Label>{language === 'en' ? 'Vehicle Model' : 'বাইক মডেল'}</Label>
                    <Input value={requestForm.bike_model} onChange={e => setRequestForm(f => ({ ...f, bike_model: e.target.value }))}
                      placeholder={language === 'en' ? 'e.g. CB Shine' : 'যেমন: CB Shine'} />
                  </div>
                </div>
                <div>
                  <Label>{t('notes')}</Label>
                  <Textarea rows={2} value={requestForm.notes} onChange={e => setRequestForm(f => ({ ...f, notes: e.target.value }))} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowRequest(false)}>{t('cancel')}</Button>
                <Button
                  disabled={submittingRequest || !requestForm.customer_name || !requestForm.customer_phone || !requestForm.part_name}
                  onClick={async () => {
                    setSubmittingRequest(true);
                    try {
                      await axios.post(`${API}/tenants/${garageSlug}/parts-request`, requestForm);
                      setRequestSuccess(true);
                      setRequestForm({ customer_name: '', customer_phone: '', part_name: '', bike_make: '', bike_model: '', notes: '' });
                    } catch {
                      // silent
                    }
                    setSubmittingRequest(false);
                  }}
                >
                  {submittingRequest ? <Spinner size="sm" /> : (language === 'en' ? 'Submit Request' : 'অনুরোধ জমা দিন')}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-slate-400">
            © {new Date().getFullYear()} {garageName}. {language === 'en' ? 'Powered by' : 'পরিচালিত'}{' '}
            <Link to="/" className="text-emerald-400 hover:text-emerald-300">MotoPotti</Link>
          </p>
        </div>
      </footer>
    </div>
  );
}
